import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AlertService, AlertStatus } from 'src/app/services/alert/alert.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SeoService } from 'src/app/services/seo/seo.service';

interface Country { dial: string; display: string; flag: string; name: string; }

@Component({
  selector: 'app-alerts-panel',
  templateUrl: './alerts-panel.component.html',
  styleUrls: ['./alerts-panel.component.scss']
})
export class AlertsPanelComponent implements OnInit {

  status: AlertStatus = {
    alertasUsadas: 0, alertasLimite: 2, isPremium: false,
    paywallActivo: false, whatsappOptIn: false,
    whatsappPhoneSet: false, correoSet: false, correoPreview: '',
    alertFrequency: 'DAILY'
  };
  candidatoId: number;
  saving = false;
  savingFreq = false;
  msg = '';
  freqMsg = '';

  // ── WhatsApp form ──────────────────────────────────────────────────────────
  showWaForm = false;
  phoneNumber = '';
  selectedDialCode = '521';
  // ── OTP flow ─────────────────────────────────────────────────────────────
  otpSent = false;
  otpCode = '';
  sendingOtp = false;
  verifyingOtp = false;
  otpMsg = '';

  // display: '+52', internal: '521'
  readonly countries: Country[] = [
    { dial: '521', display: '52', flag: '🇲🇽', name: 'México' },
    { dial: '1',   display: '1',  flag: '🇺🇸', name: 'EE.UU.' },
    { dial: '57',  display: '57', flag: '🇨🇴', name: 'Colombia' },
    { dial: '54',  display: '54', flag: '🇦🇷', name: 'Argentina' },
    { dial: '56',  display: '56', flag: '🇨🇱', name: 'Chile' },
    { dial: '51',  display: '51', flag: '🇵🇪', name: 'Perú' },
    { dial: '55',  display: '55', flag: '🇧🇷', name: 'Brasil' },
    { dial: '34',  display: '34', flag: '🇪🇸', name: 'España' },
  ];

  get frequencies() {
    return [
      { value: 'DAILY',       label: '1 vez al día',                desc: 'Después del scraper (lun-sáb)' },
      { value: 'TWICE_WEEK',  label: '2 veces / semana',            desc: 'Lunes y jueves después del scraper' },
      { value: 'TWICE_MONTH', label: '2 veces / mes',               desc: 'Días 1 y 15 después del scraper' },
    ];
  }

  get selectedCountry(): Country {
    return this.countries.find(c => c.dial === this.selectedDialCode) ?? this.countries[0];
  }

  get fullPhone(): string {
    return this.selectedDialCode + this.phoneNumber.trim().replace(/\D/g, '');
  }

  get currentFreqLabel(): string {
    return this.frequencies.find(f => f.value === this.status.alertFrequency)?.label ?? '1 vez al día';
  }

  constructor(
    private alertService: AlertService,
    private authService: AuthService,
    private seo: SeoService,
    private router: Router
  ) {
    this.seo.setDefault('Mis Alertas', 'Configura tus alertas de vacantes tech personalizadas.');
  }

  ngOnInit(): void {
    this.candidatoId = this.authService.currentUserValue?.user?.idUsuario;
    if (this.candidatoId) { this.loadStatus(); }
  }

  loadStatus(): void {
    this.alertService.getEstado(this.candidatoId).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        if (data && typeof data === 'object') {
          this.status = { ...this.status, ...data };
        }
      },
      error: (err: any) => console.error('Error cargando estado de alertas:', err)
    });
  }

  // ── OTP: enviar código ──────────────────────────────────────────────────
  sendOtpCode(): void {
    if (!this.phoneNumber.trim()) return;
    this.sendingOtp = true;
    this.otpMsg = '';
    this.alertService.sendOtp(this.fullPhone, this.candidatoId).subscribe({
      next: (res: any) => {
        this.sendingOtp = false;
        this.otpSent = true;
        this.otpMsg = res?.message || 'Código enviado. Revisa tu WhatsApp.';
      },
      error: (e) => {
        this.sendingOtp = false;
        this.otpMsg = e?.error?.message || 'Error al enviar el código.';
      }
    });
  }

  // ── OTP: verificar código ───────────────────────────────────────────────
  verifyOtpCode(andPay = false): void {
    if (!this.otpCode.trim() || this.otpCode.trim().length !== 6) {
      this.otpMsg = 'Ingresa el código de 6 dígitos.';
      return;
    }
    this.verifyingOtp = true;
    this.otpMsg = '';
    this.alertService.verifyOtp(this.fullPhone, this.otpCode.trim(), this.candidatoId).subscribe({
      next: (res: any) => {
        this.verifyingOtp = false;
        this.otpMsg = res?.message || 'WhatsApp verificado correctamente.';
        this.showWaForm = false;
        this.otpSent = false;
        this.otpCode = '';
        this.phoneNumber = '';
        this.msg = '';
        this.loadStatus();
        if (andPay) { this.router.navigate(['/planes']); }
      },
      error: (e) => {
        this.verifyingOtp = false;
        this.otpMsg = e?.error?.message || 'Código incorrecto. Intenta de nuevo.';
      }
    });
  }

  // ── Legacy save (solo guardar número sin OTP — usado para cambios admin) ─
  savePhoneAndPay(): void {
    if (!this.phoneNumber.trim()) return;
    this.sendOtpCode();
  }

  saveWhatsapp(): void {
    if (!this.phoneNumber.trim()) return;
    this.sendOtpCode();
  }

  setFrequency(freq: string): void {
    this.savingFreq = true;
    this.freqMsg = '';
    this.alertService.updateFrecuencia(this.candidatoId, freq).subscribe({
      next: () => {
        this.status.alertFrequency = freq;
        this.freqMsg = 'Frecuencia actualizada.';
        this.savingFreq = false;
      },
      error: () => { this.freqMsg = 'Error al actualizar.'; this.savingFreq = false; }
    });
  }

  goPremium(): void { this.router.navigate(['/planes']); }

  get pct(): number {
    return Math.min(100, Math.round((this.status.alertasUsadas / this.status.alertasLimite) * 100));
  }
}
